import type { MemberPermissions } from '~/utils/memberPermissions'
import type { Org } from '~/constants/orgTypes'
import apiUtil, { throwApiError } from '~/utils/apiUtils'

const { get, patch, post, del } = apiUtil()

class OrgAPI {
  constructor() {}

  async getOrg(): Promise<any> {
    try {
      const response = await get('/org')

      if (!response.ok) {
        await throwApiError(response)
      }

      const json = await response.json()
      return json
    } catch (error) {
      console.error('Error getting org:', error)
      throw error
    }
  }

  async updateOrg(org: Partial<Org>): Promise<any> {
    try {
      const response = await patch(`/org/update`, org)

      if (!response.ok) {
        await throwApiError(response)
      }

      const json = await response.json()
      return json
    } catch (error) {
      console.error('Error updating org:', error)
      throw error
    }
  }

  async listOrgUsers(
    orgId: string,
    page: number = 1,
    limit: number = 50,
  ): Promise<any> {
    try {
      const response = await get(
        `/org/${orgId}/users?page=${page}&limit=${limit}`,
      )

      if (!response.ok) {
        await throwApiError(response)
      }

      const json = await response.json()
      return json
    } catch (error) {
      console.error('Error listing org users:', error)
      throw error
    }
  }

  async inviteUser(
    orgId: string,
    data: { email: string; role: string; permissions?: MemberPermissions },
  ): Promise<any> {
    try {
      const response = await post(`/org/${orgId}/users/invite`, data)

      if (!response.ok) {
        await throwApiError(response)
      }

      const json = await response.json()
      return json
    } catch (error) {
      console.error('Error inviting user:', error)
      throw error
    }
  }

  async removeUser(orgId: string, userId: string): Promise<any> {
    try {
      const response = await del(`/org/${orgId}/users/${userId}`)

      if (!response.ok) {
        await throwApiError(response)
      }

      const json = await response.json()
      return json
    } catch (error) {
      console.error('Error removing user:', error)
      throw error
    }
  }

  async updateUserRole(
    orgId: string,
    userId: string,
    role: string,
  ): Promise<any> {
    try {
      const response = await patch(`/org/${orgId}/users/${userId}/role`, {
        role,
      })

      if (!response.ok) {
        await throwApiError(response)
      }

      const json = await response.json()
      return json
    } catch (error) {
      console.error('Error updating user role:', error)
      throw error
    }
  }

  async getSeatInfo(orgId: string): Promise<any> {
    try {
      const response = await get(`/org/${orgId}/seats`)

      if (!response.ok) {
        await throwApiError(response)
      }

      const json = await response.json()
      return json
    } catch (error) {
      console.error('Error getting seat info:', error)
      throw error
    }
  }

  async listInvites(orgId: string): Promise<any> {
    try {
      const response = await get(`/org/${orgId}/invites`)

      if (!response.ok) {
        await throwApiError(response)
      }

      const json = await response.json()
      return json
    } catch (error) {
      console.error('Error listing pending invites:', error)
      throw error
    }
  }

  async revokeInvite(orgId: string, inviteId: string): Promise<any> {
    try {
      const response = await del(`/org/${orgId}/invites/${inviteId}`)

      if (!response.ok) {
        await throwApiError(response)
      }

      const json = await response.json()
      return json
    } catch (error) {
      console.error('Error revoking invite:', error)
      throw error
    }
  }

  async cancelSubscriptionRenewal(orgId: string): Promise<any> {
    try {
      const response = await post(
        `/org/${orgId}/subscription/cancel-renewal`,
        {},
      )

      if (!response.ok) {
        await throwApiError(response)
      }

      const json = await response.json()
      return json
    } catch (error) {
      console.error('Error cancelling subscription renewal:', error)
      throw error
    }
  }

  async resumeSubscriptionRenewal(orgId: string): Promise<any> {
    try {
      const response = await post(`/org/${orgId}/subscription/resume`, {})

      if (!response.ok) {
        await throwApiError(response)
      }

      const json = await response.json()
      return json
    } catch (error) {
      console.error('Error resuming subscription renewal:', error)
      throw error
    }
  }

  async permanentlyDeleteOrg(
    orgId: string,
    confirmation: string,
    organizationName: string,
  ): Promise<any> {
    try {
      const response = await post(`/org/${orgId}/permanent-deletion`, {
        confirmation,
        organizationName,
      })

      if (!response.ok) {
        await throwApiError(response)
      }

      return await response.json()
    } catch (error) {
      console.error('Error permanently deleting organization:', error)
      throw error
    }
  }

  async transferOwnership(orgId: string, newOwnerUserId: string): Promise<any> {
    try {
      const response = await patch(`/org/${orgId}/ownership`, {
        newOwnerUserId,
      })

      if (!response.ok) {
        await throwApiError(response)
      }

      const json = await response.json()
      return json
    } catch (error) {
      console.error('Error transferring ownership:', error)
      throw error
    }
  }
}

const orgApi = new OrgAPI()

export default orgApi

export const {
  listOrgUsers,
  inviteUser,
  removeUser,
  updateUserRole,
  getSeatInfo,
  listInvites,
  revokeInvite,
  transferOwnership,
  cancelSubscriptionRenewal,
  resumeSubscriptionRenewal,
  permanentlyDeleteOrg,
} = orgApi

export async function updateSessionSettings(
  orgId: string,
  idleSessionMinutes: number,
): Promise<{ idleSessionMinutes: number; idleSessionLocked: boolean }> {
  const response = await patch(`/org/${orgId}/session-settings`, {
    idleSessionMinutes,
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    const err = new Error(
      body?.error || `HTTP error! status: ${response.status}`,
    ) as Error & { status?: number; locked?: boolean }
    err.status = response.status
    err.locked = !!body?.locked
    throw err
  }

  return response.json()
}

export async function getAdminInfo(orgId: string): Promise<any> {
  try {
    const response = await get(`/org/${orgId}/administration`)

    if (!response.ok) {
      await throwApiError(response)
    }

    const json = await response.json()
    return json
  } catch (error) {
    console.error('Error getting admin info:', error)
    throw error
  }
}

export async function updateMemberPermissions(
  orgId: string,
  userId: string,
  permissions: MemberPermissions,
): Promise<{ permissions: MemberPermissions }> {
  const response = await patch(`/org/${orgId}/users/${userId}/permissions`, {
    permissions,
  })
  if (!response.ok) await throwApiError(response)
  return response.json()
}
